import React, { createContext, useContext, useState } from 'react';
import { getVinInfo } from '../libs/db-lib';

// Define the context type
type VinDetails = {
  Make: string;
  Model: string;
  Year: string;
  Trim: string;
  VIN: string;
};

type VinCacheContextType = {
  getVinInfo: (vin: string) => Promise<VinDetails>;
};

// create the vinCache context
export const VinCacheContext = createContext<VinCacheContextType>({
  getVinInfo: (vin: string) => Promise.resolve({
    Make: '',
    Model: '',
    Year: '',
    Trim: '',
    VIN: '',
  }),
});

export function useVinCache() {
  return useContext(VinCacheContext);
}

type VinCacheProviderType = {
  children: JSX.Element;
};

const handleGetVinInfo = async (vin: string) => {
  let Make = 'The make could not be determined';
  let Model = 'The model could not be determined';
  let Year = 'The year could not be determined';
  let Trim = 'The trim could not be determined';

  const vinInfo = await getVinInfo([vin]);
  if (!vinInfo.error && Array.isArray(vinInfo) && vinInfo[0]) {
    Make = vinInfo[0].Make;
    Model = vinInfo[0].Model;
    Year = vinInfo[0].Year;
    Trim = vinInfo[0].Trim || 'The trim could not be determined';
  } else {
    console.log(`Fetching from vpic.nhtsa.dot.gov: ${vin}`)
    const response = await fetch(
      `https://vpic.nhtsa.dot.gov/api/vehicles/decodevinvalues/${vin}?format=json`,
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json();
    const result = data['Results'][0];
    Make = result.Make || Make;
    Model = result.Model || Model;
    Year = result.ModelYear || Year;
    Trim = result.Trim || Trim;

  }
  const newVehicleObj = {
    VIN: vin,
    Make: Make,
    Model: Model,
    Year: Year,
    Trim: Trim,
  };
  return newVehicleObj;
};

export function VinCacheProvider({ children }: VinCacheProviderType) {
  const [vinCache, setVinCache] = useState<{ [vin: string]: VinDetails }>({});

  const getVinInfo = async (vin: string) => {
    if (vinCache[vin]) {
      return vinCache[vin];
    } else {
      const newVinInfo = await handleGetVinInfo(vin);
      setVinCache(prev => ({
        [vin]: newVinInfo,
        ...prev,
      }));
      return newVinInfo;
    }
  };

  const provider = {
    getVinInfo,
  };

  return (
    <VinCacheContext.Provider value={provider}>
      {children}
    </VinCacheContext.Provider>
  );
}
